import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Compass, Sun, Moon, Menu, X, ShieldAlert, Dices, Star, UserRound, ChevronDown, LogOut, Coffee, Waves, Palette, Scroll, Gem } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const links = [
  { to: "/archive", label: "Archive Index" },
  { to: "/map", label: "Interactive Map" },
  { to: "/timeline", label: "Timeline" },
  { to: "/definitions", label: "Definitions" },
];

const THEMES = [
  { key: "light", label: "Light", icon: Sun, bg: "hsl(42 30% 96%)", accent: "hsl(24 90% 52%)" },
  { key: "dark", label: "Dark", icon: Moon, bg: "hsl(24 20% 4%)", accent: "hsl(24 90% 52%)" },
  { key: "espresso", label: "Espresso", icon: Coffee, bg: "hsl(25 30% 8%)", accent: "hsl(32 72% 52%)" },
  { key: "ocean", label: "Ocean Mist", icon: Waves, bg: "hsl(196 30% 95%)", accent: "hsl(188 66% 36%)" },
  { key: "papyrus", label: "Papyrus", icon: Scroll, bg: "hsl(44 42% 91%)", accent: "hsl(15 56% 42%)" },
  { key: "lapis", label: "Midnight Lapis", icon: Gem, bg: "hsl(224 46% 9%)", accent: "hsl(43 76% 56%)" },
];

const Swatch = ({ bg, accent }) => (
  <span className="relative inline-block h-4 w-4 rounded-full border border-border shrink-0" style={{ background: bg }}>
    <span className="absolute right-0 bottom-0 h-2 w-2 rounded-full" style={{ background: accent }} />
  </span>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("ae_theme") || "light");
  const [surprising, setSurprising] = useState(false);
  const navLinks = user ? links.filter((l) => l.to !== "/definitions") : links;

  const surpriseMe = async () => {
    if (surprising) return;
    setSurprising(true);
    try {
      const { data } = await api.get("/pyramids/random");
      setOpen(false);
      navigate(`/pyramid/${data.slug}`);
    } catch (e) { /* noop */ } finally {
      setSurprising(false);
    }
  };

  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove("dark", "theme-espresso", "theme-ocean", "theme-papyrus", "theme-lapis");
    if (theme === "dark") el.classList.add("dark");
    else if (theme === "espresso") el.classList.add("dark", "theme-espresso");
    else if (theme === "ocean") el.classList.add("theme-ocean");
    else if (theme === "papyrus") el.classList.add("theme-papyrus");
    else if (theme === "lapis") el.classList.add("dark", "theme-lapis");
    localStorage.setItem("ae_theme", theme);
  }, [theme]);

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" data-testid="nav-brand" className="flex items-center gap-2.5 group">
            <Compass className="h-5 w-5 text-accent transition-transform group-hover:rotate-45" />
            <span className="font-display text-sm sm:text-base font-bold tracking-[0.15em] text-foreground">
              ANOMALOUS <span className="text-accent">//</span> EARTH
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                    isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="nav-surprise-me"
              onClick={surpriseMe}
              disabled={surprising}
              className="ml-1 flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-accent hover:text-foreground transition-colors disabled:opacity-50"
            >
              <Dices className={`h-3.5 w-3.5 ${surprising ? "animate-spin" : ""}`} /> Surprise Me
            </button>
            {user ? (
              <>
                <NavLink
                  to="/my-map"
                  data-testid="nav-link-my-map"
                  className={({ isActive }) =>
                    `ml-2 flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                      isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  <Star className="h-3.5 w-3.5" /> My Map
                </NavLink>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-account-menu"
                    className="ml-2 flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-foreground hover:text-accent transition-colors outline-none"
                  >
                    <UserRound className="h-3.5 w-3.5 text-accent" />
                    <span data-testid="nav-user-name" className="max-w-[140px] truncate">{user.name || user.email}</span>
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {user.role === "admin" ? "Admin account" : "Signed in"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-my-map" onClick={() => navigate("/my-map")} className="font-mono text-xs uppercase tracking-widest cursor-pointer">
                    <Star className="h-3.5 w-3.5 mr-2 text-accent" /> My Map
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="font-mono text-xs uppercase tracking-widest cursor-pointer">
                      <ShieldAlert className="h-3.5 w-3.5 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  {(user.role === "admin" || user.role === "superadmin") && (
                    <DropdownMenuItem data-testid="menu-users" onClick={() => navigate("/admin/users")} className="font-mono text-xs uppercase tracking-widest cursor-pointer">
                      <ShieldAlert className="h-3.5 w-3.5 mr-2 text-accent" /> Users
                    </DropdownMenuItem>
                  )}
                  {user.role === "superadmin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="font-mono text-xs uppercase tracking-widest cursor-pointer">
                      <ShieldAlert className="h-3.5 w-3.5 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="font-mono text-xs uppercase tracking-widest cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="h-3.5 w-3.5 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <NavLink
                to="/account"
                data-testid="nav-link-account"
                className={({ isActive }) =>
                  `ml-2 flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                    isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                <UserRound className="h-3.5 w-3.5" /> Sign In
              </NavLink>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="theme-toggle"
                  className="ml-2 p-2 border border-border hover:bg-secondary transition-colors"
                  aria-label="Choose theme"
                >
                  {(() => { const Icon = (THEMES.find((t) => t.key === theme) || THEMES[0]).icon; return <Icon className="h-4 w-4" />; })()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel className="flex items-center gap-2"><Palette className="h-3.5 w-3.5" /> Theme</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {THEMES.map((t) => (
                  <DropdownMenuItem
                    key={t.key}
                    data-testid={`theme-option-${t.key}`}
                    onClick={() => setTheme(t.key)}
                    className={`gap-2 cursor-pointer ${theme === t.key ? "text-accent" : ""}`}
                  >
                    <Swatch bg={t.bg} accent={t.accent} /> {t.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <button data-testid="mobile-menu-toggle" className="md:hidden p-2" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background px-4 py-4 space-y-1">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 font-mono text-xs uppercase tracking-widest text-muted-foreground"
            >
              {l.label}
            </NavLink>
          ))}
          <button
            data-testid="nav-surprise-me-mobile"
            onClick={surpriseMe}
            disabled={surprising}
            className="flex w-full items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-accent disabled:opacity-50"
          >
            <Dices className={`h-3.5 w-3.5 ${surprising ? "animate-spin" : ""}`} /> Surprise Me
          </button>
          <div className="flex items-center gap-2 pt-2 flex-wrap">
            {user ? (
              <NavLink to="/my-map" data-testid="nav-link-my-map-mobile" onClick={() => setOpen(false)} className="flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-accent">
                <Star className="h-3.5 w-3.5" /> My Map
              </NavLink>
            ) : (
              <NavLink to="/account" data-testid="nav-link-account-mobile" onClick={() => setOpen(false)} className="flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-accent">
                <UserRound className="h-3.5 w-3.5" /> Sign In
              </NavLink>
            )}
            {user && (
              <button data-testid="nav-logout-mobile" onClick={() => { logout(); setOpen(false); navigate("/"); }} className="px-3 py-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Sign Out
              </button>
            )}
            <div className="flex items-center gap-1.5" data-testid="theme-toggle-mobile">
              {THEMES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`theme-option-mobile-${t.key}`}
                  onClick={() => setTheme(t.key)}
                  aria-label={t.label}
                  className={`p-2 border transition-colors ${theme === t.key ? "border-accent text-accent" : "border-border text-muted-foreground"}`}
                >
                  <t.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
          {user && (
            <p data-testid="nav-user-name-mobile" className="px-3 pt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <UserRound className="h-3.5 w-3.5 text-accent" /> Signed in as {user.name || user.email}{user.role === "admin" ? " · Admin" : ""}
            </p>
          )}
        </div>
      )}
    </header>
  );
};
